import { Injectable } from '@angular/core';

export type PaymentTab = 'Cash' | 'Card' | 'UPI' | 'Redeem Points' | 'Split';

export interface CalculateBalanceParams {
  isExchangeOrder: boolean;
  isSelected: (paymentType: string) => boolean;
  roundedTotal: number;
  cashAmount: number | null;
  cardAmount: number | null;
  upiAmount: number | null;
  redeemCreditAmount: number | null;
  refundCashAmount: number | null;
  loyaltyPaymentAmount: number | null;
}

/**
 * PaymentCalculationService
 *
 * Stateless payment-summary math (paid amount, balance, tip) for the
 * Payment Total Dialog — sibling to the existing PosCalculationService,
 * which handles cart-wide tax/discount math. Extracted verbatim from
 * DashboardComponent; every method here is a pure function of its
 * arguments, no side effects.
 *
 * `getBalanceAmount()` doubles as the extraction target for what were two
 * textually-identical methods on DashboardComponent (`getBalanceAmount()`
 * and `getAbsoluteBalance()` were both `Math.abs(this.calculateBalance())`)
 * — both now delegate to this single implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentCalculationService {
  getPaidAmount(
    activePaymentTab: PaymentTab,
    cashAmount: number | null,
    cardAmount: number | null,
    upiAmount: number | null,
    redeemCreditAmount: number | null,
    splitPaymentAmounts: { cash: number; card: number; upi: number },
  ): number {
    let paidAmount = 0;

    if (activePaymentTab === 'Split') {
      paidAmount =
        splitPaymentAmounts.cash +
        splitPaymentAmounts.card +
        splitPaymentAmounts.upi;
    } else {
      if (activePaymentTab === 'Cash' && cashAmount && cashAmount > 0) {
        paidAmount = cashAmount;
      } else if (activePaymentTab === 'Card' && cardAmount && cardAmount > 0) {
        paidAmount = cardAmount;
      } else if (activePaymentTab === 'UPI' && upiAmount && upiAmount > 0) {
        paidAmount = upiAmount;
      } else if (
        activePaymentTab === 'Redeem Points' &&
        redeemCreditAmount &&
        redeemCreditAmount > 0
      ) {
        paidAmount = redeemCreditAmount;
      }
    }

    if (
      activePaymentTab !== 'Redeem Points' &&
      redeemCreditAmount &&
      redeemCreditAmount > 0
    ) {
      paidAmount += redeemCreditAmount;
    }

    return paidAmount;
  }

  getTipAmount(): number {
    // For now, return 0 - need to be enhanced later
    return 0;
  }

  calculateBalance(params: CalculateBalanceParams): number {
    const {
      isExchangeOrder,
      isSelected,
      roundedTotal,
      cashAmount,
      cardAmount,
      upiAmount,
      redeemCreditAmount,
      refundCashAmount,
      loyaltyPaymentAmount,
    } = params;

    if (isExchangeOrder) {
      if (isSelected('Add to Credit')) {
        // When "Add to Credit" is selected, return the exchange total as the amount to add as credit
        // No other payments are considered - the whole amount becomes credit
        return roundedTotal; // This is the amount that will be added as credit
      } else {
        // For other exchange scenarios: Balance = Exchange Total - Cash Received + Refund Cash
        let exchangeBalance = roundedTotal; // Exchange total (return items)

        // Subtract cash received from customer (reduces the balance owed)
        if (isSelected('Cash') && cashAmount !== null) {
          exchangeBalance += cashAmount;
        }

        // Add refund cash given to customer (increases the balance owed)
        if (refundCashAmount !== null && refundCashAmount > 0) {
          exchangeBalance -= refundCashAmount;
        }

        return Number(exchangeBalance.toFixed(2));
      }
    } else {
      // Regular order calculation
      let paidAmount = 0;

      if (isSelected('Cash') && cashAmount !== null) {
        paidAmount += cashAmount;
      }

      if (isSelected('Card') && cardAmount !== null) {
        paidAmount += cardAmount;
      }
      if (isSelected('UPI') && upiAmount !== null) {
        paidAmount += upiAmount;
      }
      if (isSelected('Redeem Credit') && redeemCreditAmount !== null) {
        paidAmount += redeemCreditAmount;
      }
      // Add loyalty payment amount
      if (loyaltyPaymentAmount !== null && loyaltyPaymentAmount !== undefined && loyaltyPaymentAmount > 0) {
        paidAmount += loyaltyPaymentAmount;
      }
      const ordervalue = roundedTotal - paidAmount;
      return Number(ordervalue.toFixed(2));
    }
  }

  /** Shared by DashboardComponent.getBalanceAmount() and .getAbsoluteBalance()
   * (were textually-identical `Math.abs(this.calculateBalance())` bodies). */
  getBalanceAmount(balance: number): number {
    return Math.abs(balance);
  }

  getBalanceLabel(balance: number): string {
    // If balance is negative (overpayment), show "Refund Amount"
    // Otherwise show "Balance Amount"
    return balance < 0 ? 'Refund Amount' : 'Balance Amount';
  }

  getCurrentPartPaymentAmount(
    isPartPaymentCash: boolean,
    isPartPaymentCard: boolean,
    isPartPaymentUPI: boolean,
    activePaymentTab: PaymentTab,
    cashAmount: number | null,
    cardAmount: number | null,
    upiAmount: number | null,
  ): number {
    if (isPartPaymentCash && activePaymentTab === 'Cash') {
      return cashAmount || 0;
    } else if (isPartPaymentCard && activePaymentTab === 'Card') {
      return cardAmount || 0;
    } else if (isPartPaymentUPI && activePaymentTab === 'UPI') {
      return upiAmount || 0;
    }
    return 0;
  }

  getRemainingCreditAmount(
    isAnyPartPaymentEnabled: boolean,
    roundedTotal: number,
    currentPartPaymentAmount: number,
  ): number {
    if (isAnyPartPaymentEnabled) {
      return roundedTotal - currentPartPaymentAmount;
    }
    return 0;
  }
}
